import { CARD_RUNS_LABEL } from '../domain/catalog';
import type { RuntimeFilter, SortMode, StatusFilter } from '../types';

const STATUSES: StatusFilter[] = ['all', 'ok', 'warn', 'crit', 'idle'];
const STATUS_LABEL: Record<StatusFilter, string> = {
  all: 'All',
  ok: 'OK',
  warn: 'Warn',
  crit: 'Crit',
  idle: 'Idle',
};

const RUNTIME_LABEL: Record<RuntimeFilter, string> = {
  all: 'all runtimes',
  claude: 'claude code',
  hermes: 'hermes',
  n8n: 'n8n',
  cloud: 'cloud routine',
  python: 'python',
  bash: 'bash',
  external: 'external backend',
};

const SORTS: Array<{ value: SortMode; label: string }> = [
  { value: 'manual', label: 'manual (drag order)' },
  { value: 'status', label: 'status severity' },
  { value: 'runs', label: CARD_RUNS_LABEL },
  { value: 'name', label: 'name A–Z' },
];

interface FilterBarProps {
  status: StatusFilter;
  runtime: RuntimeFilter;
  /** The runtimes the board actually carries; the picker never offers one nobody runs. */
  runtimes: Array<Exclude<RuntimeFilter, 'all'>>;
  sort: SortMode;
  onStatus: (status: StatusFilter) => void;
  onRuntime: (runtime: RuntimeFilter) => void;
  onSort: (sort: SortMode) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}

/** The picker's options: what the board carries, plus the current choice so a selection is never blank. */
const runtimeChoices = (current: RuntimeFilter, available: Array<Exclude<RuntimeFilter, 'all'>>): RuntimeFilter[] =>
  current === 'all' || available.includes(current) ? ['all', ...available] : ['all', ...available, current];

/** Status × runtime × sort toolbar plus the expand/collapse shortcuts. */
const FilterBar = ({
  status,
  runtime,
  runtimes,
  sort,
  onStatus,
  onRuntime,
  onSort,
  onExpandAll,
  onCollapseAll,
}: FilterBarProps) => (
  <div className="fbar">
    <span className="fbar-group">
      <span className="fbar-l">Status</span>
      <div className="tabs" id="statusFilter">
        {STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            className="tab"
            data-active={String(value === status)}
            onClick={() => onStatus(value)}
          >
            {value === 'all' ? null : <i className={`sdot ${value}`} />}
            {STATUS_LABEL[value]}
          </button>
        ))}
      </div>
    </span>
    <span className="fbar-group">
      <label className="fbar-l" htmlFor="runtimeFilter">
        Runtime
      </label>
      <select
        className="fbar-sel"
        id="runtimeFilter"
        value={runtime}
        onChange={(e) => onRuntime(e.target.value as RuntimeFilter)}
      >
        {runtimeChoices(runtime, runtimes).map((value) => (
          <option key={value} value={value}>
            {RUNTIME_LABEL[value]}
          </option>
        ))}
      </select>
    </span>
    <span className="fbar-group">
      <label className="fbar-l" htmlFor="sortSel">
        Sort
      </label>
      <select className="fbar-sel" id="sortSel" value={sort} onChange={(e) => onSort(e.target.value as SortMode)}>
        {SORTS.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
    </span>
    <span className="fbar-group fbar-actions">
      <button type="button" className="btn btn-ghost" onClick={onExpandAll}>
        Expand all
      </button>
      <button type="button" className="btn btn-ghost" onClick={onCollapseAll}>
        Collapse all
      </button>
    </span>
  </div>
);

export default FilterBar;
